import type {Thread} from '@github-ui/diff-lines/conversation-types'

import type {ConversationComment} from '../../../page-data/payloads/conversation-comments'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import type {DiffSummary} from '@github-ui/dotcom-schema/types/pull_requests/v1/file_tree'

function compareThreadsByDiffOrder(a: Thread, b: Thread, diffPathOrderMap: Map<string, number>) {
  if (diffPathOrderMap.size === 0) return 0

  const pathA = a.path ?? a.positioning?.path
  const pathB = b.path ?? b.positioning?.path
  const orderA = pathA !== undefined ? diffPathOrderMap.get(pathA) : undefined
  const orderB = pathB !== undefined ? diffPathOrderMap.get(pathB) : undefined

  // Threads without a matching diff entry sort to the end
  if (orderA === undefined && orderB === undefined) return 0
  if (orderA === undefined) return 1
  if (orderB === undefined) return -1
  if (orderA !== orderB) return orderA - orderB

  // Same file: sort by line number
  const lineA = a.subjectType === 'FILE' ? 0 : (a.positioning?.endLine ?? a.line ?? a.subject?.endLine)
  const lineB = b.subjectType === 'FILE' ? 0 : (b.positioning?.endLine ?? b.line ?? b.subject?.endLine)
  return (lineA ?? Infinity) - (lineB ?? Infinity)
}

/**
 * Sorts thread previews by diff path order and line number. Does not mutate input array.
 * Threads are first ordered by their position in the diff (using diffPathOrderMap),
 * then by line number within the same file.
 * Threads without a matching diff entry are sorted to the end.
 */
export function sortThreadsByDiffOrder(threads: readonly Thread[], diffSummaries: readonly DiffSummary[]): Thread[] {
  const diffPathOrderMap = new Map<string, number>(diffSummaries.entries().map(([i, summary]) => [summary.path, i]))

  return threads.toSorted((a, b) => {
    return compareThreadsByDiffOrder(a, b, diffPathOrderMap)
  })
}

function getThreadSortDate(thread: Thread) {
  return getFirstComment(thread)?.createdAt ?? '0000-01-01T00:00:00Z'
}

export function sortThreadsByDate(items: Thread[], sort: 'newest-first' | 'oldest-first'): Thread[] {
  const oldestFirst = items.toSorted((a, b) => {
    const aSortDate = getThreadSortDate(a)
    const bSortDate = getThreadSortDate(b)
    return aSortDate < bSortDate ? -1 : aSortDate > bSortDate ? 1 : 0
  })
  return sort === 'newest-first' ? oldestFirst.toReversed() : oldestFirst
}

export function sortConversationCommentsByDate(
  comments: ConversationComment[],
  sort: 'newest-first' | 'oldest-first',
): ConversationComment[] {
  const oldestFirst = comments.toSorted((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0))
  return sort === 'newest-first' ? oldestFirst.toReversed() : oldestFirst
}
